import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: { terms_accepted?: boolean; terms_accepted_at?: string; emailRedirectTo?: string }) => Promise<{ error: Error | null; session: Session | null }>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  updateTermsAcceptance: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (
    email: string,
    password: string,
    metadata?: { terms_accepted?: boolean; terms_accepted_at?: string; emailRedirectTo?: string },
  ) => {
    const redirectUrl = metadata?.emailRedirectTo ?? `${window.location.origin}/auth/callback`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata?.terms_accepted != null ? { terms_accepted: metadata.terms_accepted, terms_accepted_at: metadata.terms_accepted_at } : undefined,
      },
    });

    // Persist terms acceptance to audit table (best-effort, don't block signup)
    if (!error && data?.user?.id && metadata?.terms_accepted) {
      supabase.from('terms_acceptance').insert({
        user_id: data.user.id,
        version: '1.0',
        accepted_at: metadata.terms_accepted_at ?? new Date().toISOString(),
      }).then(({ error: taErr }) => {
        if (taErr) console.warn('[auth] terms_acceptance insert failed:', taErr.message);
      });
    }

    return { error: error as Error | null, session: data?.session ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPasswordForEmail = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error as Error | null };
  };

  const updateTermsAcceptance = async () => {
    const acceptedAt = new Date().toISOString();
    const merged = {
      ...(user?.user_metadata ?? {}),
      terms_accepted: true,
      terms_accepted_at: acceptedAt,
    };
    const { error } = await supabase.auth.updateUser({ data: merged });

    // Persist to audit table (best-effort, don't block acceptance flow)
    if (!error && user?.id) {
      supabase.from('terms_acceptance').insert({
        user_id: user.id,
        version: '1.0',
        accepted_at: acceptedAt,
      }).then(({ error: taErr }) => {
        if (taErr) console.warn('[auth] terms_acceptance insert failed:', taErr.message);
      });
    }

    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, resetPasswordForEmail, updatePassword, updateTermsAcceptance }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
